'use client';

import { cn } from '@core/utils';
import { delay } from 'es-toolkit';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

type TabType = 'event1' | 'event2' | 'event3' | 'event4' | 'event5' | 'event6';

type TabConfig = {
  id: TabType;
  sectionId: string;
  label: string;
};

const TAB_HEIGHT = 46;

const TABS: TabConfig[] = [
  {
    id: 'event1',
    sectionId: 'wello-only',
    label: '정각마다 선착순',
  },
  {
    id: 'event2',
    sectionId: 'today-gift-section',
    label: '오늘의 답례품',
  },
  {
    id: 'event3',
    sectionId: 'friend-invite',
    label: '친구 초대시 경품',
  },
  {
    id: 'event4',
    sectionId: 'repeat-donor',
    label: '돌아온 웰로투',
  },
  {
    id: 'event5',
    sectionId: 'sns-event',
    label: 'SNS 후기 이벤트',
  },
  {
    id: 'event6',
    sectionId: 'photo-review',
    label: '포토후기 1천원',
  },
];

let isClicking = false;

const useActiveTab = (
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
) => {
  const [activeTab, setActiveTab] = useState<TabType>('event1');

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const initialTab = TABS.find((tab) => tab.sectionId === hash);
    if (initialTab) {
      setActiveTab(initialTab.id);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClicking) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            const tab = TABS.find((t) => t.sectionId === entry.target.id);
            if (tab) setActiveTab(tab.id);
          }
        }
      },
      { rootMargin: `-${TAB_HEIGHT}px 0px -50% 0px` },
    );

    TABS.forEach(({ sectionId }) => {
      const el = document.getElementById(sectionId);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTabToCenter = useCallback(
    (tabId: TabType) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const tabElement = container.querySelector(
        `[data-tab-id="${tabId}"]`,
      ) as HTMLElement;
      if (!tabElement) return;

      const containerWidth = container.offsetWidth;
      const tabLeft = tabElement.offsetLeft;
      const tabWidth = tabElement.offsetWidth;
      const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    },
    [scrollContainerRef],
  );

  const handleTabClick = async (tabId: TabType) => {
    setActiveTab(tabId);
    scrollTabToCenter(tabId);
    isClicking = true;
    await delay(1000);
    isClicking = false;
  };

  return { activeTab, handleTabClick, scrollTabToCenter };
};

const TabItem = ({
  tab,
  isActive,
  onClick,
}: {
  tab: TabConfig;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <Link
      href={`#${tab.sectionId}`}
      replace
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      aria-controls={tab.sectionId}
      tabIndex={isActive ? 0 : -1}
      data-tab-id={tab.id}
      className={cn(
        'flex h-46 shrink-0 items-center justify-center whitespace-nowrap px-16 outline-none transition-all',
        isActive
          ? 'border-b-[3px] border-[#E4302C]'
          : 'border-b border-Gray-300',
      )}
    >
      <span
        className={cn(
          'typo-body4',
          isActive ? 'font-700 text-Gray-black' : 'font-600 text-Gray-500',
        )}
      >
        {tab.label}
      </span>
    </Link>
  );
};

export const Tab = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { activeTab, handleTabClick, scrollTabToCenter } =
    useActiveTab(scrollContainerRef);

  useEffect(() => {
    scrollTabToCenter(activeTab);
  }, [activeTab, scrollTabToCenter]);

  return (
    <div
      className="sticky top-0 z-[2] w-full bg-Gray-white"
      role="tablist"
      aria-label="이벤트 네비게이션"
    >
      <div
        ref={scrollContainerRef}
        className="mx-auto flex w-full max-w-[750px] overflow-x-auto hide-scrollbar"
      >
        {TABS.map((tab) => (
          <TabItem
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            onClick={() => handleTabClick(tab.id)}
          />
        ))}
      </div>
    </div>
  );
};
